import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Input, Select, Space, Typography, message } from 'antd';
import type { QaqcPipeWelder, VwPersonnel, QaqcPipeWps, Company } from '../../../../types';
import { insertData, updateData, getTableNextSequenceValue } from '../../../../services/api';
import { useGetPersonnel, useGetQaqcPipeWps, useGetCompanies, useGetProfessions } from '../../../../hooks/genericHooks';
import Button from '../../../../components/common/Button';

const { Title, Text } = Typography;

export type QaqcPipeWelderMode = 'create' | 'edit';

interface QaqcPipeWelderCreateProps {
  mode?: QaqcPipeWelderMode;
  initialValues?: Partial<QaqcPipeWelder> | null;
  onSaved?: (saved: QaqcPipeWelder | any) => void;
  onCancel?: () => void;
}

const employmentStatusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'terminated', label: 'Terminated' },
];

export default function QaqcPipeWelderCreate({
  mode = 'create',
  initialValues,
  onSaved,
  onCancel,
  onBack,
  viewOnly,
}: QaqcPipeWelderCreateProps & { onBack?: () => void; viewOnly?: boolean }) {
  const [form] = Form.useForm<QaqcPipeWelder>();
  const isEdit = mode === 'edit';
  const isViewOnly = !!viewOnly;
  // Adjustable control widths (px). Tweak as needed.
  const fieldWidths = {
    company: 260,
    occupation: 160,
    personnel: 300,
    welderName: 100,
    employmentStatus: 100,
    wpsList: 1000,
  } as const;
  // Adjustable UI sizes
  const uiSizes = {
    titleFontSize: 18, // adjust page title font size here
  } as const;
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(undefined);
  const [selectedOccupation, setSelectedOccupation] = useState<string | undefined>(undefined);
  const { data: personnelResp, isLoading: loadingPersonnel } = useGetPersonnel();
  const { data: wpsResp } = useGetQaqcPipeWps();
  const { data: companiesResp } = useGetCompanies();
  const { data: professionsResp } = useGetProfessions();

  type PersOption = { value: string; label: string; meta: VwPersonnel };
  const personnelOptions: PersOption[] = useMemo(() => {
    const list = (personnelResp?.data as unknown as VwPersonnel[]) || [];
    let filtered = selectedCompanyId ? list.filter(p => (p.company_id ?? null) === selectedCompanyId) : list;
    if (selectedOccupation && selectedOccupation !== '__all__') {
      const occ = selectedOccupation.toLowerCase();
      filtered = filtered.filter(p => (p.occupation || '').toLowerCase() === occ);
    }
    return filtered.map((p: VwPersonnel) => ({
      value: p.pers_id,
      label: p.pers_id ? `${p.pers_id} - ${p.first_name} ${p.last_name}` : `${p.first_name} ${p.last_name}`,
      meta: p,
    }));
  }, [personnelResp, selectedCompanyId, selectedOccupation]);

  const companyOptions = useMemo(() => {
    const list = (companiesResp?.data as unknown as Company[]) || [];
    return list.map(c => ({ value: c.id, label: c.name }));
  }, [companiesResp]);

  const occupationOptions = useMemo(() => {
    const list = (professionsResp?.data as any[]) || [];
    const set = new Set<string>();
    list.forEach((p: any) => { if (p?.profession) set.add(String(p.profession)); });
    const opts = Array.from(set).sort((a,b)=>a.localeCompare(b)).map(o => ({ value: o, label: o }));
    return [{ value: '__all__', label: 'All' }, ...opts];
  }, [professionsResp]);

  const wpsOptions = useMemo(() => {
    const list = (wpsResp?.data as unknown as QaqcPipeWps[]) || [];
    return list.map((r) => ({ value: r.wps_name, label: r.wps_name }));
  }, [wpsResp]);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        project_id: initialValues.project_id ?? 1,
        first_name: initialValues.first_name,
        last_name: initialValues.last_name,
        company_id: initialValues.company_id,
        personnel_id: initialValues.personnel_id,
        welder_name: initialValues.welder_name,
        wps_list: initialValues.wps_list,
        employment_status: initialValues.employment_status,
      } as any);
      setSelectedCompanyId(initialValues.company_id);
      // try set occupation from selected personnel if found
      const list = (personnelResp?.data as unknown as VwPersonnel[]) || [];
      const pers = list.find(p => p.pers_id === initialValues.personnel_id);
      if (pers?.occupation) setSelectedOccupation(pers.occupation);
    } else {
      form.setFieldsValue({ project_id: 1, employment_status: 'active', wps_list: [] } as any);
      // Suggest next welder code like W-00001 (peek without consuming the sequence)
      (async () => {
        try {
          const nextVal = await getTableNextSequenceValue('qaqc_pipe_welder', 'id');
          if (Number.isFinite(nextVal)) {
            const code = `W-${String(nextVal as number).padStart(5, '0')}`;
            // Only set if field is still empty
            const current = form.getFieldValue('welder_name');
            if (!current) form.setFieldsValue({ welder_name: code } as any);
          }
        } catch {}
      })();
    }
  }, [initialValues, form, personnelResp]);

  const handlePersonnelChange = (persId?: string) => {
    const selected = (personnelOptions || []).find((o: PersOption) => o.value === persId)?.meta as VwPersonnel | undefined;
    if (selected) {
      form.setFieldsValue({
        first_name: selected.first_name,
        last_name: selected.last_name,
        company_id: selected.company_id ?? undefined,
        personnel_id: selected.pers_id,
      } as any);
      // welder_name is auto-generated; do not derive from personnel
    }
  };

  const handleCompanyChange = (companyId?: number) => {
    setSelectedCompanyId(companyId);
    // Clear personnel on company change
    form.setFieldsValue({ personnel_id: undefined } as any);
  };

  const handleOccupationChange = (val?: string) => {
    setSelectedOccupation(val && val !== '__all__' ? val : undefined);
    form.setFieldsValue({ personnel_id: undefined } as any);
  };

  const onSubmit = async () => {
    try {
      // Ensure we have a welder code; if missing, generate a fresh suggestion (non-consuming peek)
      let wl = form.getFieldValue('welder_name');
      if (!wl) {
        const nextVal = await getTableNextSequenceValue('qaqc_pipe_welder', 'id');
        if (Number.isFinite(nextVal)) {
          wl = `W-${String(nextVal as number).padStart(5, '0')}`;
          form.setFieldsValue({ welder_name: wl } as any);
        }
      }
      const values = await form.validateFields();
      const payload: any = {
        project_id: Number(values.project_id) || 1,
        first_name: values.first_name,
        last_name: values.last_name,
        company_id: Number(values.company_id),
        personnel_id: values.personnel_id,
        welder_name: values.welder_name,
        wps_list: values.wps_list || [],
        employment_status: values.employment_status,
      };

      if (mode === 'edit' && initialValues?.id) {
        await updateData('qaqc_pipe_welder', initialValues.id, payload, 'id');
        message.success('Welder updated');
        try { window.dispatchEvent(new Event('qaqc_pipe_welder:changed')); } catch {}
        onSaved?.({ id: initialValues.id, ...payload });
      } else {
        const resp = await insertData('qaqc_pipe_welder', payload);
        message.success('Welder created');
        try { window.dispatchEvent(new Event('qaqc_pipe_welder:changed')); } catch {}
        onSaved?.({ id: resp?.ids?.[0], ...payload });
        form.resetFields();
      }
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.error || 'Save failed');
    }
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: uiSizes.titleFontSize }}>
            {(mode === 'edit' || isViewOnly) && onBack && (
              <a onClick={onBack} style={{ marginRight: 16, fontSize: 16 }}>&larr; Back</a>
            )}
            {isViewOnly ? 'View Welder' : (mode === 'edit' ? 'Edit Welder' : 'Create Welder')}
          </span>
        </div>
      }
      bordered
      size="small"
    >
      <Form form={form} layout="vertical">
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          {/* Use flex so control-specific widths are respected */}
          <Space style={{ gap: 12, display: 'flex', flexWrap: 'wrap' }}>
            <Form.Item label="Company" name="company_id" rules={[{ required: true, message: 'Select company' }]} style={{ marginBottom: 0 }}>
              <Select
                showSearch
                allowClear
                options={companyOptions}
                onChange={handleCompanyChange}
                onClear={() => {
                  setSelectedCompanyId(undefined);
                  form.setFieldsValue({ company_id: undefined, personnel_id: undefined } as any);
                }}
                optionFilterProp="label"
                filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
                placeholder="Select company"
                style={{ width: fieldWidths.company }}
                disabled={isEdit || isViewOnly}
              />
            </Form.Item>

            <Form.Item label="Occupation" style={{ marginBottom: 0 }}>
              <Select
                showSearch
                allowClear
                options={occupationOptions}
                value={selectedOccupation}
                onChange={handleOccupationChange}
                onClear={() => {
                  setSelectedOccupation(undefined);
                  form.setFieldsValue({ personnel_id: undefined } as any);
                }}
                optionFilterProp="label"
                filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
                placeholder="Filter occupation"
                style={{ width: fieldWidths.occupation }}
                disabled={isEdit || isViewOnly}
              />
            </Form.Item>

            <Form.Item label="Personnel" name="personnel_id" rules={[{ required: true, message: 'Select personnel' }]} style={{ marginBottom: 0 }}>
              <Select
                showSearch
                loading={loadingPersonnel}
                allowClear
                options={personnelOptions}
                onChange={handlePersonnelChange}
                onClear={() => {
                  form.setFieldsValue({ personnel_id: undefined } as any);
                }}
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const label = String(option?.label || '').toLowerCase();
                  const tokens = input.trim().toLowerCase().split(/\s+/).filter(Boolean);
                  return tokens.every(tok => label.includes(tok));
                }}
                placeholder="Select personnel"
                style={{ width: fieldWidths.personnel }}
                disabled={isEdit || isViewOnly}
              />
            </Form.Item>

            <Form.Item label="Welder Name" name="welder_name" rules={[{ required: true, max: 30 }]} style={{ marginBottom: 0 }}>
              <Input maxLength={30} placeholder="Auto-generated" disabled style={{ width: fieldWidths.welderName }} />
            </Form.Item>

            <Form.Item label="Employment Status" name="employment_status" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
              <Select options={employmentStatusOptions} disabled={isEdit || isViewOnly} style={{ width: fieldWidths.employmentStatus }} />
            </Form.Item>
          </Space>

          {/* Hidden fields to carry derived values */}
          <Form.Item name="first_name" hidden rules={[{ required: true }]}>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="last_name" hidden rules={[{ required: true }]}>
            <Input type="hidden" />
          </Form.Item>

          <Form.Item label="WPS List" name="wps_list" rules={[{ required: true }]}>
            <Select
              mode="multiple"
              options={wpsOptions}
              placeholder="Select WPS codes"
              showSearch
              filterOption={(input, option) => (option?.label as string)?.toLowerCase().includes(input.toLowerCase())}
              style={{ width: fieldWidths.wpsList }}
              disabled={isViewOnly}
            />
          </Form.Item>

          {!isViewOnly && (
            <Space>
              <Button action={mode === 'edit' ? 'edit' : 'add'} type="primary" onClick={onSubmit}>{mode === 'edit' ? 'Update' : 'Create'}</Button>
              {onCancel && (
                <Button onClick={onCancel}>Cancel</Button>
              )}
            </Space>
          )}
        </Space>
      </Form>
    </Card>
  );
}
